import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, ShieldCheck } from 'lucide-react';

// Zone coordinates are FACE-RELATIVE percentages (0-100 within the face bounding
// box, hairline -> chin). They get mapped into the detected face_box on the photo.
const ZONES = {
  forehead: { label: 'Forehead', x: 50, y: 18, rx: 32, ry: 11, attrs: ['acne', 'oiliness', 'texture', 'fine_lines', 'congestion'] },
  t_zone: { label: 'T-Zone / Nose', x: 50, y: 52, rx: 12, ry: 17, attrs: ['blackheads', 'pores', 'oiliness', 'congestion'] },
  under_eyes: { label: 'Under Eyes', x: 72, y: 51, rx: 13, ry: 6, attrs: ['dark_circles', 'eye_bags', 'puffiness', 'fine_lines'] },
  left_cheek: { label: 'Left Cheek', x: 21, y: 62, rx: 14, ry: 12, attrs: ['redness', 'dark_spots', 'uneven_tone', 'texture', 'dryness'] },
  right_cheek: { label: 'Right Cheek', x: 79, y: 62, rx: 14, ry: 12, attrs: ['redness', 'dark_spots', 'uneven_tone', 'texture', 'dryness'] },
  chin: { label: 'Chin', x: 50, y: 92, rx: 12, ry: 8, attrs: ['acne', 'whiteheads', 'congestion', 'irritation'] },
  jawline: { label: 'Jawline', x: 17, y: 82, rx: 10, ry: 9, attrs: ['acne', 'cysts', 'acne_scars', 'sagging'] },
};

const DEFAULT_FACE_BOX = { x: 18, y: 8, w: 64, h: 80 };

const zoneColor = (s) => (s >= 75 ? '#5BBF7A' : s >= 50 ? '#E8B93E' : '#E4573D');
const zoneLabel = (s) => (s >= 75 ? 'Healthy' : s >= 50 ? 'Watch' : 'Concern');

// Deterministic satellite positions for a zone's attribute points, spread on the zone ellipse
const satellitePositions = (zone, count) => {
  const pts = [];
  for (let i = 0; i < count; i++) {
    const angle = (i / count) * 2 * Math.PI - Math.PI / 2;
    pts.push({
      x: zone.x + Math.cos(angle) * zone.rx * 0.62,
      y: zone.y + Math.sin(angle) * zone.ry * 0.62,
    });
  }
  return pts;
};

const ScanOverlay = ({ imageSrc, zoneScores, scores = {}, detectedIssues = [], attrMeta = {}, faceBox }) => {
  const [phase, setPhase] = useState('scanning'); // scanning | done
  const [activeZone, setActiveZone] = useState(null);
  const [showHeat, setShowHeat] = useState(true);
  const [showPoints, setShowPoints] = useState(true);
  const [imgReady, setImgReady] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setPhase('done'), 2600);
    return () => clearTimeout(t);
  }, []);

  const onImgLoad = useCallback(() => setImgReady(true), []);

  if (!imageSrc || !zoneScores) return null;

  const fb = faceBox && faceBox.w >= 15 && faceBox.h >= 15 ? faceBox : DEFAULT_FACE_BOX;
  // Map a face-relative point (0-100 within face box) to image percentages
  const mapX = (fx) => fb.x + (fx / 100) * fb.w;
  const mapY = (fy) => fb.y + (fy / 100) * fb.h;

  const zoneKeys = Object.keys(ZONES).filter((z) => zoneScores[z] != null);
  const totalPoints = zoneKeys.reduce((n, z) => n + ZONES[z].attrs.filter((a) => scores[a] != null).length, 0);
  const active = activeZone ? ZONES[activeZone] : null;
  const activeScore = activeZone ? zoneScores[activeZone] : null;
  const zoneMatch = (z) => z.replace(/[\s-]/g, '_').toLowerCase();
  const activeIssues = activeZone
    ? detectedIssues.filter((it) => (it.zones || []).some((z) => {
        const n = zoneMatch(z);
        return n.includes(activeZone.replace('_', '')) || activeZone.includes(n) ||
          (activeZone.endsWith('cheek') && n.includes('cheek')) ||
          (activeZone === 't_zone' && (n.includes('nose') || n.includes('t_zone'))) ||
          (activeZone === 'under_eyes' && n.includes('eye'));
      }))
    : [];

  return (
    <div className="mb-6" data-testid="scan-overlay">
      {/* No cropping: the image renders at natural aspect ratio (w-full h-auto),
          so percentage positions map 1:1 onto the photo. */}
      <div className="relative rounded-2xl overflow-hidden bg-[#161b17] select-none">
        <img ref={imgRef} src={imageSrc} alt="Your scan" className="w-full h-auto block" onLoad={onImgLoad} draggable={false} />

        {/* Heat zones */}
        {phase === 'done' && showHeat && imgReady && (
          <svg className="absolute inset-0 w-full h-full pointer-events-none scan-fade-in" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              {zoneKeys.map((z) => (
                <radialGradient key={z} id={`heat-${z}`}>
                  <stop offset="0%" stopColor={zoneColor(zoneScores[z])} stopOpacity="0.42" />
                  <stop offset="70%" stopColor={zoneColor(zoneScores[z])} stopOpacity="0.18" />
                  <stop offset="100%" stopColor={zoneColor(zoneScores[z])} stopOpacity="0" />
                </radialGradient>
              ))}
            </defs>
            {zoneKeys.map((z) => (
              <ellipse
                key={z}
                cx={mapX(ZONES[z].x)}
                cy={mapY(ZONES[z].y)}
                rx={(ZONES[z].rx / 100) * fb.w}
                ry={(ZONES[z].ry / 100) * fb.h}
                fill={`url(#heat-${z})`}
              />
            ))}
          </svg>
        )}

        {/* Scanning animation */}
        {phase === 'scanning' && (
          <div className="absolute inset-0" data-testid="scan-animation">
            <div className="absolute inset-0 scan-grid" />
            <div className="scan-sweep-line" />
            <div className="absolute bottom-4 left-0 right-0 text-center">
              <span className="inline-flex items-center gap-2 bg-black/60 backdrop-blur-sm text-white text-xs font-semibold px-4 py-2 rounded-full">
                <span className="w-2 h-2 rounded-full bg-[#5BBF7A] animate-pulse" />
                Analyzing 30 clinical points…
              </span>
            </div>
          </div>
        )}

        {/* Satellite clinical points — one dot per analyzed attribute (the "30 points") */}
        {phase === 'done' && showPoints && zoneKeys.map((z) => {
          const zn = ZONES[z];
          const zoneAttrs = zn.attrs.filter((a) => scores[a] != null);
          const sats = satellitePositions(zn, zoneAttrs.length);
          return zoneAttrs.map((a, j) => (
            <span
              key={`${z}-${a}`}
              className="absolute -translate-x-1/2 -translate-y-1/2 scan-marker-pop pointer-events-none"
              style={{ left: `${mapX(sats[j].x)}%`, top: `${mapY(sats[j].y)}%`, animationDelay: `${0.3 + j * 0.05}s` }}
              data-testid={`clinical-point-${z}-${a}`}
            >
              <span
                className="block w-2.5 h-2.5 rounded-full border border-white/90"
                style={{ backgroundColor: zoneColor(scores[a]), boxShadow: '0 1px 4px rgba(0,0,0,0.5)' }}
              />
            </span>
          ));
        })}

        {/* Numbered zone markers */}
        {phase === 'done' && zoneKeys.map((z, i) => {
          const zn = ZONES[z];
          const c = zoneColor(zoneScores[z]);
          const isActive = activeZone === z;
          return (
            <button
              key={z}
              onClick={() => setActiveZone(isActive ? null : z)}
              className="absolute -translate-x-1/2 -translate-y-1/2 scan-marker-pop"
              style={{ left: `${mapX(zn.x)}%`, top: `${mapY(zn.y)}%`, animationDelay: `${i * 0.12}s` }}
              data-testid={`zone-marker-${z}`}
              aria-label={`${zn.label} score ${zoneScores[z]}`}
            >
              <span className="absolute inset-0 rounded-full scan-marker-ring" style={{ backgroundColor: c }} />
              <span
                className={`relative flex items-center justify-center w-8 h-8 rounded-full text-[13px] font-bold text-white border-2 leading-none transition-transform ${isActive ? 'scale-125 border-white' : 'border-white/70'}`}
                style={{ backgroundColor: c, boxShadow: '0 2px 10px rgba(0,0,0,0.45)' }}
              >
                {i + 1}
              </span>
            </button>
          );
        })}

        {/* Points counter chip */}
        {phase === 'done' && (
          <div className="absolute top-3 left-3 z-10">
            <span className="inline-flex items-center gap-1.5 bg-black/60 backdrop-blur-sm text-white text-[11px] font-semibold px-3 py-1.5 rounded-full" data-testid="points-counter">
              <ShieldCheck className="w-3.5 h-3.5 text-[#5BBF7A]" />
              {totalPoints} clinical points mapped
            </span>
          </div>
        )}

        {/* Zone detail sheet */}
        {phase === 'done' && active && (
          <div className="absolute bottom-0 left-0 right-0 z-10 bg-black/78 backdrop-blur-md text-white p-4 scan-sheet-up" data-testid="zone-detail-sheet">
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="text-sm font-bold">{active.label}</p>
                <p className="text-xs font-semibold" style={{ color: zoneColor(activeScore) }}>
                  Zone score {activeScore}/100 · {zoneLabel(activeScore)}
                </p>
              </div>
              <button onClick={() => setActiveZone(null)} className="p-1 rounded-full hover:bg-white/10" data-testid="zone-detail-close">
                <X className="w-4 h-4 text-white/70" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
              {active.attrs.filter((a) => scores[a] != null).map((a) => (
                <div key={a} className="flex items-center justify-between gap-2 text-xs">
                  <span className="text-white/80">{attrMeta[a] || a}</span>
                  <span className="font-bold" style={{ color: zoneColor(scores[a]) }}>{scores[a]}</span>
                </div>
              ))}
            </div>
            {activeIssues.length > 0 && (
              <div className="mt-2 pt-2 border-t border-white/15 flex flex-wrap gap-1.5">
                {activeIssues.map((it, idx) => (
                  <span key={idx} className="text-[10px] font-semibold bg-white/12 px-2 py-0.5 rounded-full capitalize">
                    {it.issue} · {it.severity}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Legend + controls */}
      {phase === 'done' && (
        <div className="flex flex-wrap items-center justify-between gap-2 mt-3 scan-fade-in">
          <div className="flex items-center gap-3 text-[11px] text-[#8C7A76]">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: '#5BBF7A' }} /> Healthy</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: '#E8B93E' }} /> Watch</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: '#E4573D' }} /> Concern</span>
            <span className="hidden sm:flex items-center gap-1 text-[#FF6B4A]"><ShieldCheck className="w-3.5 h-3.5" /> Tap a marker for zone details</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowPoints((v) => !v)}
              className={`text-[11px] font-semibold px-3 py-1 rounded-full border transition-colors ${showPoints ? 'bg-[#A388EE] text-white border-[#A388EE]' : 'text-[#A388EE] border-[#A388EE]/40'}`}
              data-testid="points-toggle"
            >
              Points {showPoints ? 'on' : 'off'}
            </button>
            <button
              onClick={() => setShowHeat((v) => !v)}
              className={`text-[11px] font-semibold px-3 py-1 rounded-full border transition-colors ${showHeat ? 'bg-[#FF6B4A] text-white border-[#FF6B4A]' : 'text-[#FF6B4A] border-[#FF6B4A]/40'}`}
              data-testid="heatmap-toggle"
            >
              Heat map {showHeat ? 'on' : 'off'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScanOverlay;
